import { getEnvByHostname } from '../utils/env';
import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Space, message, Row, Col, Divider, InputNumber, Descriptions, Tag, Tooltip } from 'antd';
import { DollarOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, BankOutlined, SwapOutlined, GlobalOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { getChannelLogo, buildChannelOptions } from '../utils/channelLogos';
import { testApi } from '../services/api';
import { buildRequestBody, generatePayinOrderId, testTemplates } from '../utils/testTemplates';
import { transfiMethodOptions, transfiBankCodeOptions } from '../utils/transfiCatalog';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageCache } from '../contexts/PageCacheContext';

const DEPOSIT_METHOD_CONFIG = {
  VIRTUAL_ACCOUNT: { icon: <GlobalOutlined />, color: 'blue',   label: 'Virtual Account' },
  BANK_TRANSFER:   { icon: <BankOutlined />,   color: 'green',  label: 'Bank Transfer' },
  PIX:             { icon: <SwapOutlined />,   color: 'purple', label: 'PIX' },
  IDEAL:           { icon: <BankOutlined />,   color: 'orange',  label: 'iDEAL' },
  INTERAC:         { icon: <SwapOutlined />,   color: 'cyan',    label: 'Interac' },
  QRIS:            { icon: <GlobalOutlined />, color: 'cyan',    label: 'QRIS' },
  LINKAJA:         { icon: <GlobalOutlined />, color: 'red',     label: 'LinkAja' },
  OVO:             { icon: <GlobalOutlined />, color: 'volcano', label: 'OVO' },
  GOPAY:           { icon: <GlobalOutlined />, color: 'lime',    label: 'GoPay' },
  DANA:            { icon: <GlobalOutlined />, color: 'geekblue',label: 'DANA' },
  SHOPEEPAY:       { icon: <GlobalOutlined />, color: 'magenta', label: 'ShopeePay' },
};

const depositMethodOption = (value) => {
  const cfg = DEPOSIT_METHOD_CONFIG[value] || {};
  return {
    value,
    label: (
      <Space size={6}>
        <Tag icon={cfg.icon} color={cfg.color || 'default'} style={{ margin: 0, fontSize: 12 }}>{cfg.label || value}</Tag>
      </Space>
    ),
  };
};

export default function Payin() {
  const [form] = Form.useForm();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [userChannels, setUserChannels] = useState([]);
  const [currentChannelIndex, setCurrentChannelIndex] = useState(0);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedDepositMethod, setSelectedDepositMethod] = useState(null);
  const [, setFormTick] = useState(0);
  const { cache, save: saveCache } = usePageCache('payin');

  useEffect(() => {
    loadUserChannels();
    if (cache) {
      if (cache.result) setResult(cache.result);
      if (cache.selectedChannel) setSelectedChannel(cache.selectedChannel);
      if (cache.currentChannelIndex != null) setCurrentChannelIndex(cache.currentChannelIndex);
      if (cache.formValues) form.setFieldsValue(cache.formValues);
    } else {
      const savedData = localStorage.getItem('payinFormData');
      if (savedData) { try { form.setFieldsValue(JSON.parse(savedData)); } catch (e) {} }
    }
  }, [form]);

  useEffect(() => {
    return () => {
      saveCache({ result, selectedChannel, currentChannelIndex, formValues: form.getFieldsValue() });
    };
  });

  const loadUserChannels = async () => {
    try {
      const { data } = await testApi.getChannelCurrencies();
      const map = data.data?.responseBody || data.data || {};
      const channels = Object.keys(map).map(channelCode => ({ channelCode, currencies: [...new Set(map[channelCode] || [])] }));
      setUserChannels(channels);
      if (channels.length > 0) {
        form.setFieldsValue({ channelCode: channels[0].channelCode, currency: channels[0].currencies[0] });
        setSelectedChannel(channels[0].channelCode);
        // 初始化时自动生成 externalOrderId
        if (!form.getFieldValue('externalOrderId')) {
          form.setFieldsValue({ externalOrderId: generatePayinOrderId(channels[0].channelCode) });
        }
      }
    } catch (e) { setUserChannels([]); }
  };

  const saveFormData = () => localStorage.setItem('payinFormData', JSON.stringify(form.getFieldsValue()));

  // INSWITCH: 币种与充值方式强绑定（MXN→SPEI, BRL→PIX）
  const INSWITCH_CURRENCY_DEPOSIT_METHOD = { MXN: 'SPEI', BRL: 'PIX' };

  const handleValuesChange = (changed) => {
    const channelCode = form.getFieldValue('channelCode');
    // INSWITCH 渠道：根据币种锁定充值方式
    if (channelCode === 'INSWITCH' && changed && Object.prototype.hasOwnProperty.call(changed, 'currency')) {
      const fixedMethod = INSWITCH_CURRENCY_DEPOSIT_METHOD[changed.currency];
      if (fixedMethod && form.getFieldValue('depositMethod') !== fixedMethod) {
        form.setFieldsValue({ depositMethod: fixedMethod });
      }
      // MXN 证件类型固定为 ruc
      if (changed.currency === 'MXN') {
        form.setFieldsValue({ documentType: 'ruc' });
      }
    }
    // TRANSFI：币种变化时清空充值方式与 bankCode（不同币种支付方式不同，避免残留非法值）
    if (channelCode === 'TRANSFI' && changed && Object.prototype.hasOwnProperty.call(changed, 'currency')) {
      form.setFieldsValue({ depositMethod: undefined, bankCode: undefined });
    }
    const dm = form.getFieldValue('depositMethod');
    if (dm !== selectedDepositMethod) setSelectedDepositMethod(dm);
    // 触发重渲染以驱动 extParam 字段的 showWhen 显隐（如 TransFi onramp/payin 差异化字段）
    setFormTick(t => t + 1);
    saveFormData();
  };

  const handleChannelChange = (channelCode) => {
    setSelectedChannel(channelCode);
    const idx = userChannels.findIndex(c => c.channelCode === channelCode);
    if (idx >= 0) setCurrentChannelIndex(idx);
    const ch = userChannels[idx];
    if (ch?.currencies.length > 0) form.setFieldsValue({ currency: ch.currencies[0] });
    // INSWITCH：切渠道后按当前币种锁定充值方式
    if (channelCode === 'INSWITCH') {
      const fixed = INSWITCH_CURRENCY_DEPOSIT_METHOD[form.getFieldValue('currency')];
      if (fixed) form.setFieldsValue({ depositMethod: fixed });
      if (form.getFieldValue('currency') === 'MXN') form.setFieldsValue({ documentType: 'ruc' });
    }
    form.setFieldsValue({ externalOrderId: generatePayinOrderId(channelCode) });
    saveFormData();
  };

  const renderExtParamFields = () => {
    if (!selectedChannel) return null;
    const fields = (testTemplates.PAYIN?.channels?.[selectedChannel] || []).filter(f => f.group === 'extParam');
    if (!fields.length) return null;
    return (
      <>
        <Divider style={{ margin: '16px 0' }} />
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>⚙️ {t('extParams') || '扩展参数'}</div>
        <Row gutter={[16, 16]}>
          {fields.filter(f => {
            // showWhen 条件过滤（onramp/payin 差异化字段显隐，支持数组值）
            if (f.showWhen) {
              const watchedValue = form.getFieldValue(f.showWhen.field);
              if (Array.isArray(f.showWhen.value)) {
                return f.showWhen.value.includes(watchedValue);
              }
              return watchedValue === f.showWhen.value;
            }
            return true;
          }).map(field => {
            let isRequired = !!field.required;
            if (field.requiredWhen) {
              const watchedValue = form.getFieldValue(field.requiredWhen.field);
              isRequired = watchedValue === field.requiredWhen.value;
            }
            if (field.requiredWhenNot) {
              const watchedValue = form.getFieldValue(field.requiredWhenNot.field);
              isRequired = watchedValue !== field.requiredWhenNot.value;
            }
            const isDisabled = field.disabledWhen
              && form.getFieldValue(field.disabledWhen.field) === field.disabledWhen.value;
            // TRANSFI payin bankCode：按币种给出银行下拉（IDR/PHP/VND 小清单）
            const transfiBankOptions = (selectedChannel === 'TRANSFI' && field.name === 'bankCode')
              ? transfiBankCodeOptions(form.getFieldValue('currency'), 'deposit') : null;
            return (
            <Col xs={24} sm={12} key={field.name}>
              <Form.Item
                label={<Space size={4}><span>{t(field.name) || field.label}</span>{field.tip && <Tooltip title={t(field.tip)}><span style={{ cursor: 'help', color: '#1890ff', fontSize: 12 }}>?</span></Tooltip>}</Space>}
                name={field.name} rules={[{ required: isRequired }]} initialValue={field.default}>
                {transfiBankOptions
                  ? <Select showSearch placeholder={field.placeholder} disabled={isDisabled} options={transfiBankOptions.map(o => ({ label: o, value: o }))} />
                  : field.type === 'select'
                  ? <Select placeholder={field.placeholder} disabled={isDisabled} options={(field.options || []).map(o => ({ label: o, value: o }))} />
                  : <Input placeholder={field.placeholder} disabled={isDisabled} />}
              </Form.Item>
            </Col>
          )})}
        </Row>
      </>
    );
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // 自动生成 externalOrderId（表单中无此字段，需手动注入）
      if (!values.externalOrderId) {
        values.externalOrderId = generatePayinOrderId(values.channelCode);
      }
      const requestBody = buildRequestBody('PAYIN', values, values.channelCode, values.currency);
      const response = await testApi.executeTest({
        testType: 'PAYIN', channelCode: values.channelCode,
        businessPartner: values.businessPartner || 'DEMO_ACCOUNT',
        currency: values.currency, env: values.env || getEnvByHostname(),
        executor: localStorage.getItem('username'), requestBody: JSON.stringify(requestBody)
      });
      const res = response.data;
      if (res.data?.responseBody && typeof res.data.responseBody === 'string') {
        try { res.data.responseBody = JSON.parse(res.data.responseBody); } catch (e) {}
      }
      setResult(res);
      const rbMsg = res.data?.responseBody?.msg;
      if (res.code === '200') message.success(rbMsg || t('payinSuccess'));
      else message.warning(`${t('payinFailed')}: ${rbMsg || res.msg || t('failed')}`);
    } catch (error) {
      message.error(t('payinFailed') + ': ' + (error.response?.data?.msg || error.message));
      setResult(error.response?.data || { error: error.message });
    } finally { setLoading(false); }
  };

  return (
    <div style={{ padding: '16px 24px', height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <div className="fade-in-up">
        <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', padding: '12px 24px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)' }}>
          <Space size={12}>
            <DollarOutlined style={{ color: '#fff', fontSize: 20 }} />
            <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{t('payin')} {userChannels.length > 0 && `- ${getChannelLogo(userChannels[currentChannelIndex]?.channelCode).name}`}</span>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => { form.resetFields(); setResult(null); }} type="text" style={{ color: '#fff' }}>{t('reset')}</Button>
            {userChannels.length > 1 && (
              <Button onClick={() => {
                const next = (currentChannelIndex + 1) % userChannels.length;
                setCurrentChannelIndex(next);
                form.setFieldsValue({ channelCode: userChannels[next].channelCode, currency: userChannels[next].currencies[0] });
                setSelectedChannel(userChannels[next].channelCode);
              }} type="text" style={{ color: '#fff' }}>{t('switchChannel')}</Button>
            )}
          </Space>
        </div>

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Card className="modern-card" bodyStyle={{ padding: '24px' }}>
              <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={handleValuesChange}>
                <Form.Item name="businessPartner" hidden><Input /></Form.Item>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>⚙️ {t('basicConfig')}</div>
                <Form.Item label={t('channel')} name="channelCode" rules={[{ required: true }]}>
                  <Select placeholder={t('selectChannel')} onChange={handleChannelChange}
                    options={buildChannelOptions(userChannels.map(c => c.channelCode))} />
                </Form.Item>
                <Form.Item label={t('currency')} name="currency" rules={[{ required: true }]} initialValue="USD">
                  <Select options={(userChannels.find(c => c.channelCode === form.getFieldValue('channelCode'))?.currencies || []).map(c => ({ label: c, value: c }))} />
                </Form.Item>
                <Divider style={{ margin: '16px 0' }} />
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>💰 {t('payinParams')}</div>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label={t('amount')} name="amount" rules={[{ required: true }]}>
                      <InputNumber style={{ width: '100%' }} placeholder={t('amount')} min={0} step={0.01} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('depositMethod')} name="depositMethod" rules={[{ required: true }]}>
                      <Select placeholder={t('depositMethod')} showSearch
                        disabled={selectedChannel === 'INSWITCH'}
                        options={(() => {
                        const ch = selectedChannel || form.getFieldValue('channelCode');
                        // INSWITCH 币种锁定充值方式
                        if (ch === 'INSWITCH') {
                          const fixed = INSWITCH_CURRENCY_DEPOSIT_METHOD[form.getFieldValue('currency')];
                          if (fixed) return [depositMethodOption(fixed)];
                        }
                        // TRANSFI：按币种动态给出 BANK_TRANSFER(若支持)+钱包编码+CRYPTO_ONRAMP
                        if (ch === 'TRANSFI') {
                          return transfiMethodOptions(form.getFieldValue('currency'), 'deposit').map(depositMethodOption);
                        }
                        const channelFields = testTemplates.PAYIN?.channels?.[ch];
                        if (channelFields) {
                          const dmField = channelFields.find(f => f.name === 'depositMethod');
                          if (dmField?.options) return dmField.options.map(depositMethodOption);
                        }
                        return ['VIRTUAL_ACCOUNT', 'BANK_TRANSFER', 'PIX', 'IDEAL'].map(depositMethodOption);
                      })()} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label={t('callbackUrl')} name="callbackUrl" initialValue="https://test-platform.fiat.com/callback/payin">
                  <Input disabled style={{ background: '#f5f5f5', color: '#666' }} />
                </Form.Item>
                <Form.Item
                  label={selectedChannel === 'GENIAL' ? `* ${t('accountId')}` : `${t('accountId')} (${t('optional')})`}
                  name="accountId"
                  rules={selectedChannel === 'GENIAL' ? [{ required: true, message: 'accountId is required for GENIAL' }] : []}
                >
                  <Input placeholder={t('accountId')} />
                </Form.Item>
                {renderExtParamFields()}
                <Button type="primary" htmlType="submit" icon={<DollarOutlined />} loading={loading} size="large" block className="primary-button" style={{ height: 48, marginTop: 16 }}>
                  {loading ? t('paying') : t('submitPayin')}
                </Button>
              </Form>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card className="modern-card" title={`📋 ${t('payinResult')}`} bodyStyle={{ padding: '24px' }}>
              {result ? (
                <div>
                  {(() => {
                    const rb = result.data?.responseBody;
                    const d = rb?.data;
                    const isSuccess = rb?.code === '200' || result.code === '200';
                    return (
                      <div style={{ marginBottom: 24 }}>
                        <Descriptions column={1} bordered size="middle" title={t('payinOrderDetail')}>
                          <Descriptions.Item label={t('status')}>
                            {isSuccess && d?.status === 'SUCCESS'
                              ? <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 14, padding: '4px 12px' }}>SUCCESS</Tag>
                              : isSuccess && d?.status === 'PROCESSING'
                              ? <Tag color="processing" icon={<CheckCircleOutlined />} style={{ fontSize: 14, padding: '4px 12px' }}>PROCESSING</Tag>
                              : <Tag color="error" icon={<CloseCircleOutlined />} style={{ fontSize: 14, padding: '4px 12px' }}>FAILED</Tag>}
                          </Descriptions.Item>
                          {rb?.code && rb.code !== '200' && <Descriptions.Item label={t('errorCode')}><Tag color="error">{rb.code}</Tag></Descriptions.Item>}
                          {rb?.msg && <Descriptions.Item label={t('errorMsg')}><span style={{ color: '#ff4d4f' }}>{rb.msg}</span></Descriptions.Item>}
                          {d?.orderId && <Descriptions.Item label={t('orderId')}><code style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4 }}>{d.orderId}</code></Descriptions.Item>}
                          {d?.amount && <Descriptions.Item label={t('amount')}><span style={{ fontSize: 16, fontWeight: 600, color: '#10b981' }}>{d.amount} {d.currency}</span></Descriptions.Item>}
                        </Descriptions>
                      </div>
                    );
                  })()}
                  <Divider style={{ margin: '16px 0' }}>{t('response')}</Divider>
                  <div className="json-viewer"><ReactJson src={result} collapsed={1} theme="rjv-default" displayDataTypes={false} displayObjectSize={false} enableClipboard={true} /></div>
                </div>
              ) : (
                <div style={{ padding: 60, textAlign: 'center', color: '#999', background: '#f9fafb', borderRadius: 8, border: '2px dashed #d1d5db' }}>
                  <DollarOutlined style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                  <div>{t('submitToShowPayinResult')}</div>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
